import type { ReactNode } from "react"
import {
	IconArrowRightOutline18 as ArrowRight,
	IconAtom2Outline18 as Atom,
	IconCopy2Outline18 as CopyIcon,
	IconEnvelopeOutline18 as Envelope,
	IconEyeOutline18 as Eye,
	IconThumbsDownOutline18 as ThumbsDown,
	IconThumbsUpOutline18 as ThumbsUp,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import { Avatar, Badge, Button, Input } from "@productlane/ui"
import { GitHubIcon } from "@productlane/ui/src/icons/project/github-icon"

import { isDevEnvironment } from "@/env"
import { NotFound } from "@/screens/not-found"
import { DesignIssueHeader } from "./design-issue-header"

const GREETING = "Hi! We're here to help. Do you have any questions?"

const SUBTITLE_TODAY = "This will reach our team directly."
const SUBTITLE_NEW = "Our AI agent answers first. The team reads every chat."

const REPLY_TODAY =
	"The help center for this workspace hasn't been published yet, so I can't pull up answers right now, but a human will follow up with you."
const REPLY_NEW = "Thanks for writing in. The team replies right here."

function WidgetFrame({
	subtitle,
	children,
}: {
	subtitle: string
	children: ReactNode
}) {
	return (
		<div className="flex h-[640px] w-[380px] flex-none flex-col overflow-hidden rounded-[22px] bg-white shadow-lg ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10">
			<div className="flex items-center gap-3 px-5 pb-3 pt-5">
				<Avatar name="Raphael Fleckenstein" size="sm" className="flex-none" />
				<div className="min-w-0">
					<div className="text-[15px] font-semibold tracking-tight text-primary">
						Chat with the team
					</div>
					<div className="text-[13px] tracking-tight text-secondary">
						{subtitle}
					</div>
				</div>
			</div>
			<div className="flex flex-1 flex-col gap-4 overflow-y-auto px-5 pb-4 pt-2">
				<div className="text-center text-xs text-tertiary">Today 11:47 AM</div>
				<div className="text-[0.95rem] tracking-tight text-primary">
					{GREETING}
				</div>
				<div className="ml-auto w-fit rounded-md bg-gray-100 px-2.5 py-1.5 text-[0.94rem] text-primary dark:bg-gray-800">
					test
				</div>
				{children}
			</div>
			<EmailPrompt />
		</div>
	)
}

function AgentReply({
	text,
	muted = false,
	showFeedback = false,
}: {
	text: string
	muted?: boolean
	showFeedback?: boolean
}) {
	return (
		<div>
			<div className="mb-[5px] flex items-center gap-2">
				<Atom className="size-[17px] flex-none text-gray-900 dark:text-white" />
				<span className="text-sm font-semibold text-primary">AI Agent</span>
				<span className="text-xs text-tertiary">now</span>
			</div>
			<div
				className={cn(
					"text-[0.95rem] leading-relaxed tracking-tight",
					muted ? "text-secondary" : "text-primary",
				)}
			>
				{text}
			</div>
			{showFeedback && (
				<div className="mt-2 flex items-center gap-3 text-tertiary">
					<ThumbsUp className="size-4 flex-none" />
					<ThumbsDown className="size-4 flex-none" />
					<CopyIcon className="size-4 flex-none" />
				</div>
			)}
		</div>
	)
}

function EmailPrompt() {
	return (
		<div className="m-3 mt-0 rounded-xl bg-gray-50 p-3.5 ring-1 ring-black/5 dark:bg-gray-850 dark:ring-white/10">
			<div className="flex items-start gap-2.5">
				<div className="flex size-6 flex-none items-center justify-center rounded-md bg-white shadow-sm ring-1 ring-black/10 dark:bg-gray-900 dark:ring-white/10">
					<Envelope className="size-3.5 flex-none text-secondary" />
				</div>
				<div className="min-w-0">
					<div className="text-sm font-medium tracking-tight text-primary">
						One step before this reaches the team
					</div>
					<div className="mt-0.5 text-xs leading-relaxed text-secondary">
						We answer here, and by email if you close the chat.
					</div>
				</div>
			</div>
			<div className="mt-3 flex items-start gap-2">
				<Input
					name="email"
					className="flex-1"
					inputClassName="h-9 rounded-lg"
					placeholder="your@email.com"
				/>
				<Button className="h-9 rounded-full">Send message</Button>
			</div>
		</div>
	)
}

// Rendered only when the person chatting is a member of the workspace.
function TeamOnlySetupCard() {
	return (
		<div className="rounded-xl border border-dashed border-gray-300 bg-gray-50/60 p-3.5 dark:border-gray-700 dark:bg-gray-850">
			<Badge variant="important" size="sm">
				<Eye className="size-3 flex-none" />
				Only your team sees this
			</Badge>
			<div className="mt-2.5 text-sm font-medium tracking-tight text-primary">
				Publish your help center so the agent can answer
			</div>
			<div className="mt-0.5 text-xs leading-relaxed text-secondary">
				It learns your product from your docs and your GitHub repo.
			</div>
			<div className="mt-3 flex flex-wrap items-center gap-2">
				<Button size="sm" iconRight={ArrowRight}>
					Publish help center
				</Button>
				<Button size="sm" variant="outline" Icon={GitHubIcon}>
					Connect GitHub
				</Button>
			</div>
		</div>
	)
}

function Column({
	n,
	title,
	note,
	children,
}: {
	n: string
	title: string
	note: string
	children: ReactNode
}) {
	return (
		<div className="w-[380px] flex-none">
			<div className="mb-3 h-[60px]">
				<div className="text-sm font-medium text-primary">
					{n}. {title}
				</div>
				<div className="mt-0.5 text-[13px] text-secondary">{note}</div>
			</div>
			{children}
		</div>
	)
}

const SPEC: { title: string; body: string }[] = [
	{
		title: "Honest header",
		body: `With the AI agent on, the subtitle says who answers: "${SUBTITLE_NEW}" With the agent off, it keeps "${SUBTITLE_TODAY}"`,
	},
	{
		title: "Visitors never see setup talk",
		body: `With no published help center, the agent sends one friendly handoff line: "${REPLY_NEW}" The prompt section emptyKnowledgeBaseSection changes; nothing mentions the workspace, the help center, or publishing.`,
	},
	{
		title: "Setup note for the team only",
		body: "When the person chatting is a workspace member, a dashed card follows the reply. It names the fix and links to it: Publish help center opens the help center settings, Connect GitHub opens the GitHub integration. It stays until the help center is live.",
	},
	{
		title: "Full contrast replies",
		body: "Agent text renders in text-primary. aiChatContentClasses already forces this on main, so the grey in the screenshot is gone.",
	},
	{
		title: "No rating on a handoff",
		body: "The thumbs and copy row hides on a reply that only hands off to the team. There is no answer to rate.",
	},
	{
		title: "Translations",
		body: "apps/widget-app is localized: the new subtitle and the team card strings need keys in en/common.json and a real translation in every packages/lib/src/translations/<lang>/common.json.",
	},
]

export function LiveChatFirstImpressionPage() {
	if (!isDevEnvironment) return <NotFound />

	return (
		<div className="h-full overflow-y-auto bg-gray-50 dark:bg-gray-900">
			<div className="mx-auto max-w-[1240px] px-6 py-10 pb-24">
				<div className="max-w-[760px]">
					<DesignIssueHeader
						identifier="PRO-10593"
						title="Bad first impression testing the live chat and agent"
						url="https://linear.app/productlane/issue/PRO-10593"
						summary="A new customer who tests their widget before publishing a help center gets an agent reply about the missing setup, under a header that promises the team. This design shows today's chat, the same chat as a visitor sees it, and the version the team sees while testing, with a setup note only they get."
					/>
				</div>

				<div className="mt-10 flex flex-wrap items-start gap-8">
					<Column
						n="1"
						title="Today"
						note="Setup talk to a visitor, grey text, a rating row on a handoff."
					>
						<WidgetFrame subtitle={SUBTITLE_TODAY}>
							<AgentReply text={REPLY_TODAY} muted showFeedback />
						</WidgetFrame>
					</Column>
					<Column
						n="2"
						title="What a visitor sees"
						note="The header names the agent. The reply hands off in one line."
					>
						<WidgetFrame subtitle={SUBTITLE_NEW}>
							<AgentReply text={REPLY_NEW} />
						</WidgetFrame>
					</Column>
					<Column
						n="3"
						title="What your team sees while testing"
						note="Same chat, plus a setup note only workspace members get."
					>
						<WidgetFrame subtitle={SUBTITLE_NEW}>
							<AgentReply text={REPLY_NEW} />
							<TeamOnlySetupCard />
						</WidgetFrame>
					</Column>
				</div>

				<div className="mt-16 max-w-[760px]">
					<div className="text-lg font-semibold text-primary">Spec</div>
					<div className="mt-4 space-y-4">
						{SPEC.map((item) => (
							<div key={item.title}>
								<div className="text-sm font-medium text-primary">
									{item.title}
								</div>
								<div className="mt-0.5 text-[13px] leading-relaxed text-secondary">
									{item.body}
								</div>
							</div>
						))}
					</div>
				</div>
			</div>
		</div>
	)
}
